// One-shot handoff slot for the post-onboarding tour: "where the user was
// actually headed before the tour borrowed the screen".
//
// The tour can only run on the chat surface (see tour-surface.ts), so both the
// welcome gate and the auto-start redirect there. Whatever destination that
// redirect displaced is parked here and TourProvider.finish() consumes it once
// the tour ends - completed or skipped - so the user still lands where they
// asked to go:
//   - a public job "Apply" that ran through onboarding (`jobs:` post-auth
//     target parked by completeOnboarding)
//   - onboarding's final "Find jobs" CTA (/(app)/jobs, issue #257)
//   - the route a migrated user opened the app on before the tour pulled them
//     to chat
//
// Module state only, deliberately not persisted: if the app dies mid-tour the
// destination is dropped rather than leaking into a later session.
let postTourDestination: string | null = null;

/**
 * Last-writer-wins setter for an explicit, known destination (onboarding
 * completion decides this before any redirect happens).
 */
export function setPostTourDestination(href: string) {
  postTourDestination = href;
}

/**
 * First-writer-wins park used by the redirects themselves. An explicit
 * destination already chosen by onboarding (the job the user applied to) must
 * survive a later route the user merely passed through, so this never
 * overwrites a slot that is already filled, and ignores empty hrefs so callers
 * can pass getReturnableTourDestination() straight through.
 */
export function parkPostTourDestination(href: string | null | undefined) {
  if (!href) return;
  if (postTourDestination) return;
  postTourDestination = href;
}

export function consumePostTourDestination() {
  const href = postTourDestination;
  postTourDestination = null;
  return href;
}

/**
 * Drop a parked destination without navigating to it. Used when the tour is
 * replayed on demand from Settings: that run must end where it started, not
 * hijack a destination parked by an earlier, abandoned redirect.
 */
export function clearPostTourDestination() {
  postTourDestination = null;
}
